import type {
  ActionConfig,
  ActionDescription,
  TriggerDescription,
  IntegrationDescription,
  AutomationConfig,
} from "@smartsuite/automation-engine-api";
import { translateAction } from "../../../locales/i18n";
import { t } from "i18next";

/** Synthetic actionId/integrationId for the Conditional Logic (branches) badge
 * shown in the automations list. Mirrors the "loop" badge -- a branches block
 * renders as a single badge (its own icon), not by expanding nested actions. */
export const CONDITIONAL_LOGIC_BADGE = "conditional-logic";

export const getGroupActions = (
  automation: AutomationConfig,
): ActionConfig[] => {
  return automation.actionGroups.flatMap((group) => {
    if (group.config.oneofKind === "actions") {
      return group.config.actions.actions;
    }

    if (group.config.oneofKind === "loop") {
      // Not a flatten: a loop renders as a single synthetic "loop" badge
      // action in the icon list, rather than expanding its nested actions.
      return [
        {
          label: "loop",
          dependencies: [],
          credentialId: "",
          inputs: [],
          actionId: "",
          actionName: "Looping",
          actionType: "loop",
          actionReference: {
            integrationId: "loop",
            actionId: "loop",
            instanceId: NaN,
          },
        },
      ];
    }

    if (group.config.oneofKind === "branches") {
      // Single synthetic Conditional Logic badge (same as loop). Without this
      // the branches block contributed no icon, so a CL-only automation fell
      // back to the generic default logo (STR-14148).
      return [
        {
          label: CONDITIONAL_LOGIC_BADGE,
          dependencies: [],
          credentialId: "",
          inputs: [],
          actionId: "",
          actionName: "Conditional Logic",
          actionType: "branches",
          actionReference: {
            integrationId: CONDITIONAL_LOGIC_BADGE,
            actionId: CONDITIONAL_LOGIC_BADGE,
            instanceId: NaN,
          },
        },
      ];
    }

    return [];
  });
};

export const getTriggerIntegration = (
  integrationList: IntegrationDescription[],
  automation: AutomationConfig,
): IntegrationDescription | undefined => {
  return integrationList.find(
    (i) =>
      i.integrationId === automation.trigger?.triggerReference?.integrationId,
  );
};

export const getTrigger = (
  integrationList: IntegrationDescription[],
  automation: AutomationConfig,
): TriggerDescription | undefined => {
  return getTriggerIntegration(integrationList, automation)?.triggers.find(
    (t) => t.triggerId === automation.trigger?.triggerReference?.triggerId,
  );
};

export const getActionIntegration = (
  integrationList: IntegrationDescription[],
  action?: ActionConfig,
): IntegrationDescription | undefined => {
  return integrationList.find(
    (i) => i.integrationId === action?.actionReference?.integrationId,
  );
};

export const getAction = (
  integrationList: IntegrationDescription[],
  action?: ActionConfig,
): ActionDescription | undefined => {
  return getActionIntegration(integrationList, action)?.actions.find(
    (t) => t.actionId === action?.actionReference?.actionId,
  );
};

export const getActionTooltipText = (
  integrationList: IntegrationDescription[],
  action: ActionConfig,
): string => {
  if (action.actionReference?.actionId === "loop") {
    return t("general.looping");
  }
  if (action.actionReference?.actionId === CONDITIONAL_LOGIC_BADGE) {
    return String(t("branches.default-block-name"));
  }
  return `${
    getActionIntegration(integrationList, action)?.label ?? ""
  }: ${translateAction(getAction(integrationList, action) ?? {})}`;
};
